import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import Autocomplete from './ui/autocomplete';

import axios from 'axios';
import {
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Calendar,
  MapPin,
  Settings,
  FileText,
  FileCheck,
  Download,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Shield,
  Plus,
  Edit,
  Trash,
  Eye,
  Filter,
  Image,
  RefreshCw,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Componente para exibir foto do registro
const FotoRegistro = ({ registro }) => {
  const [showModal, setShowModal] = useState(false);
  const [imageError, setImageError] = useState(false);

  const fotoSrc = registro.foto_url
    ? `${API}${registro.foto_url}`
    : registro.foto_comprovante;
  const temFoto = !!(registro.foto_url || registro.foto_comprovante);

  console.log('FotoRegistro - Dados recebidos:', {
    tem_foto: temFoto,
    foto_url: registro.foto_url ? registro.foto_url.substring(0, 60) : 'N/A',
    tem_base64: !!registro.foto_comprovante,
  });

  if (!temFoto) {
    return (
      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-gray-300">
        <Image className="h-6 w-6 text-gray-400" />
      </div>
    );
  }

  return (
    <>
      {/* Miniatura clicável */}
      <div 
        className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all border-2 border-gray-300"
        onClick={() => setShowModal(true)}
        title="Clique para ver a foto em tamanho maior"
      >
        {imageError ? (
          <div className="w-full h-full flex items-center justify-center bg-red-100">
            <Image className="h-6 w-6 text-red-400" />
          </div>
        ) : (
          <img 
            src={fotoSrc} 
            alt="Foto do registro"
            className="w-full h-full object-cover bg-white"
            onError={(e) => {
              console.error('Erro ao carregar imagem:', e);
              setImageError(true);
            }}
            onLoad={() => {
              console.log('✅ Imagem carregada com sucesso!');
            }}
            style={{ 
              minWidth: '100%', 
              minHeight: '100%',
              backgroundColor: 'white'
            }}
          />
        )}
      </div>
      
      {/* Modal para visualização ampliada */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Foto do Registro</h3>
                <p className="text-sm text-gray-600">
                  {registro.funcionario_nome} - {formatarDataHora(registro.data_hora)}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowModal(false)}
              >
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-4">
              <img 
                src={fotoSrc}
                alt="Foto do registro"
                className="w-full max-h-96 object-contain rounded"
                onError={(e) => {
                  console.error('Erro no modal:', e);
                }}
              />
              <div className="mt-4 text-sm text-gray-600">
                <p><strong>Tipo:</strong> {registro.tipo_marcacao}</p>
                <p><strong>Origem:</strong> {registro.origem}</p>
                <p><strong>Data/Hora:</strong> {formatarDataHora(registro.data_hora)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Função para formatar data/hora
const formatarDataHora = (dateString) => {
  return new Date(dateString).toLocaleString('pt-BR');
};

const formatarTamanhoArquivo = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return 'Tamanho não informado';
  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getDocumentoPreviewKind = (doc) => {
  const mime = (doc?.arquivo_tipo || '').toLowerCase();
  const nome = (doc?.arquivo_nome || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(nome)) return 'image';
  if (mime.includes('pdf') || nome.endsWith('.pdf')) return 'pdf';
  return 'other';
};

const getDocumentoStatusConfig = (status) => {
  switch (status) {
    case 'pendente':
      return { label: 'Pendente', badgeClass: 'bg-amber-100 text-amber-800 border-amber-200' };
    case 'visualizado':
      return { label: 'Visualizado', badgeClass: 'bg-sky-100 text-sky-800 border-sky-200' };
    case 'aprovado':
      return { label: 'Aprovado', badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
    case 'reprovado':
      return { label: 'Reprovado', badgeClass: 'bg-rose-100 text-rose-800 border-rose-200' };
    default:
      return { label: status || 'Sem status', badgeClass: 'bg-slate-100 text-slate-700 border-slate-200' };
  }
};

const formatarDataCurta = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatarHoraCurta = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const getSolicitacaoTipoConfig = (tipo) => {
  switch (tipo) {
    case 'ponto_esquecido':
      return {
        label: 'Ponto esquecido',
        badgeClass: 'bg-violet-100 text-violet-800 border-violet-200',
        accentClass: 'border-violet-200',
        surfaceClass: 'bg-violet-50',
      };
    case 'correcao':
      return {
        label: 'Correção de registro',
        badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
        accentClass: 'border-blue-200',
        surfaceClass: 'bg-blue-50',
      };
    case 'abono_falta':
      return {
        label: 'Abono de falta',
        badgeClass: 'bg-orange-100 text-orange-800 border-orange-200',
        accentClass: 'border-orange-200',
        surfaceClass: 'bg-orange-50',
      };
    case 'banco_horas':
      return {
        label: 'Banco de horas',
        badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        accentClass: 'border-emerald-200',
        surfaceClass: 'bg-emerald-50',
      };
    default:
      return {
        label: tipo || 'Solicitação',
        badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
        accentClass: 'border-slate-200',
        surfaceClass: 'bg-slate-50',
      };
  }
};

// ============================================================================
// TABELA GENÉRICA DO RELATÓRIO — mesmo design de tabela usado na memória de
// cálculo do INSS (cabeçalho slate-100, bordas, linhas zebradas), mas aqui
// sem lógica específica por tipo de relatório: renderiza qualquer JSON de
// /timetracking/relatorios/* automaticamente (chave/valor + listas viram tabelas).
// ============================================================================
const prettyChave = (k) => k.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());

const prettyValor = (k, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'number') {
    return Number.isInteger(v) ? String(v) : v.toFixed(2).replace('.', ',');
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      if (!isNaN(d)) return d.toLocaleString('pt-BR');
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      return v.split('-').reverse().join('/');
    }
  }
  return String(v);
};

const JsonTabela = ({ dados }) => {
  if (!dados || typeof dados !== 'object') return null;

  const entradas = Object.entries(dados);
  const listas = entradas.filter(([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object');
  const objetos = entradas.filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v));
  const escalares = entradas.filter(
    ([, v]) => v === null || typeof v !== 'object' || (Array.isArray(v) && v.length === 0)
  );

  return (
    <div className="space-y-4">
      {escalares.length > 0 && (
        <table className="w-full border rounded">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-2">Campo</th>
              <th className="text-right p-2">Valor</th>
            </tr>
          </thead>
          <tbody>
            {escalares.map(([k, v]) => (
              <tr key={k} className="border-t">
                <td className="p-2 text-gray-600">{prettyChave(k)}</td>
                <td className="p-2 text-right font-medium">{prettyValor(k, v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {objetos.map(([k, v]) => (
        <div key={k}>
          <h4 className="font-semibold text-slate-700 mb-1">{prettyChave(k)}</h4>
          <table className="w-full border rounded">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-2">Campo</th>
                <th className="text-right p-2">Valor</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(v).map(([k2, v2]) => (
                <tr key={k2} className="border-t">
                  <td className="p-2 text-gray-600">{prettyChave(k2)}</td>
                  <td className="p-2 text-right font-medium">{prettyValor(k2, v2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {listas.map(([k, arr]) => {
        const colunas = Array.from(arr.reduce((set, row) => {
          Object.keys(row).forEach((c) => set.add(c));
          return set;
        }, new Set()));
        return (
          <div key={k}>
            <h4 className="font-semibold text-slate-700 mb-1">{prettyChave(k)}</h4>
            <div className="overflow-x-auto">
              <table className="w-full border rounded">
                <thead className="bg-slate-100">
                  <tr>
                    {colunas.map((c) => (
                      <th key={c} className="text-left p-2">{prettyChave(c)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {arr.map((row, i) => (
                    <tr key={i} className="border-t">
                      {colunas.map((c) => (
                        <td key={c} className="p-2">{prettyValor(c, row[c])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TimeTrackingAdminPage = () => {
  console.log('🚀🚀🚀 TimeTrackingAdminPage CARREGADO - VERSÃO COM LOGS');
  const { user } = useAuth();
  const ALL_PAGE_TABS = ['dashboard', 'ajustes', 'funcionarios', 'afd', 'aej', 'configuracoes', 'relatorios'];
  const allowedTabs = ALL_PAGE_TABS;
  const visibleTabs = [...ALL_PAGE_TABS, 'documentos-enviados'];
  const { toast } = useToast();
  
  // Estados principais
  const [activeTab, setActiveTab] = useState('dashboard');
  console.log('📋 Estado inicial activeTab:', activeTab);
  const [dashboardData, setDashboardData] = useState({});
  const [ajustesPendentes, setAjustesPendentes] = useState([]);
  const [pontosEsquecidos, setPontosEsquecidos] = useState([]);
  
  // NOVO: Estados para abonos
  const [abonos, setAbonos] = useState([]);
  const [filtroTipoSolicitacao, setFiltroTipoSolicitacao] = useState('todos'); // todos, ponto_esquecido, abono_falta, banco_horas
  
  // NOVO: Estados para abas internas de Ajustes
  const [abaAjustes, setAbaAjustes] = useState('pendentes'); // 'pendentes' ou 'confirmados'
  const [ajustesConfirmados, setAjustesConfirmados] = useState([]);
  const [pontosConfirmados, setPontosConfirmados] = useState([]);
  const [abonosConfirmados, setAbonosConfirmados] = useState([]);

  const [funcionarios, setFuncionarios] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedTabs, setLoadedTabs] = useState({
    dashboard: false,
    ajustes: false,
    funcionarios: false,
    configuracoes: false,
    documentos: false,
  });
  
  // Estados para documentos enviados
  const [documentosEnviados, setDocumentosEnviados] = useState([]);
  const [carregandoDocumentos, setCarregandoDocumentos] = useState(false);
  const [filtroStatusDoc, setFiltroStatusDoc] = useState('');
  const [observacoesDoc, setObservacoesDoc] = useState({});
  const [documentPreviews, setDocumentPreviews] = useState({});
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState({});
  const [documentosResumo, setDocumentosResumo] = useState({ total: 0, pendente: 0, visualizado: 0, aprovado: 0, reprovado: 0 });
  const [documentosPagination, setDocumentosPagination] = useState({
    page: 1,
    per_page: 12,
    total: 0,
    total_pages: 1,
    has_prev: false,
    has_next: false,
  });
  const previewUrlsRef = useRef({});
  
  // Estados para filtros de documentos
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroFuncionarioId, setFiltroFuncionarioId] = useState('');
  const [filtroFuncionarioLabel, setFiltroFuncionarioLabel] = useState('');
  const [filtroTipoDocumento, setFiltroTipoDocumento] = useState('');
  const [tiposDocumentos, setTiposDocumentos] = useState([]);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    dataInicio: new Date().toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0],
    funcionario: 'todos'
  });

  useEffect(() => {
    const hasPermission = user?.is_admin || (
      user?.granular_permissions?.find(m => m.module_id === 'funcionarios')
        ?.tabs?.find(t => t.id === 'ponto_admin')?.enabled
    );
    
    if (hasPermission) {
      carregarDashboardInicial();
    }
  }, [user]);

  useEffect(() => {
    const hasPermission = user?.is_admin || (
      user?.granular_permissions?.find(m => m.module_id === 'funcionarios')
        ?.tabs?.find(t => t.id === 'ponto_admin')?.enabled
    );

    if (!hasPermission) return;

    if (activeTab === 'ajustes' && !loadedTabs.ajustes) {
      carregarDadosAjustes();
    }

    if ((activeTab === 'funcionarios' || activeTab === 'relatorios') && !loadedTabs.funcionarios) {
      carregarFuncionarios();
    }

    if (activeTab === 'configuracoes' && !loadedTabs.configuracoes) {
      carregarConfiguracoes();
    }
  }, [activeTab, loadedTabs.ajustes, loadedTabs.configuracoes, loadedTabs.funcionarios, user]);

  // Carregar documentos quando abrir a aba ou alterar o status
  useEffect(() => {
    if (activeTab !== 'documentos-enviados') return;

    if (!loadedTabs.documentos) {
      carregarDadosDocumentos();
      return;
    }

    carregarDocumentosEnviados();
  }, [activeTab, filtroStatusDoc, loadedTabs.documentos]);

  const markTabAsLoaded = (tabId) => {
    setLoadedTabs((prev) => (prev[tabId] ? prev : { ...prev, [tabId]: true }));
  };

  const carregarDashboardInicial = async () => {
    setLoading(true);
    try {
      await carregarDashboard();
      markTabAsLoaded('dashboard');
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({ title: "Erro", description: 'Erro ao carregar dados administrativos', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const carregarDadosAjustes = async () => {
    try {
      await Promise.all([
        carregarAjustesPendentes(),
        carregarAjustesConfirmados(),
        carregarPontosEsquecidos(),
        carregarAbonos()
      ]);
      markTabAsLoaded('ajustes');
    } catch (error) {
      console.error('Erro ao carregar dados de ajustes:', error);
      toast({ title: "Erro", description: 'Erro ao carregar ajustes administrativos', variant: "destructive" });
    }
  };

  const carregarDadosDocumentos = async () => {
    try {
      await Promise.all([
        carregarTiposDocumentos(),
        carregarDocumentosEnviados()
      ]);
      markTabAsLoaded('documentos');
    } catch (error) {
      console.error('Erro ao carregar dados de documentos:', error);
    }
  };

  const carregarDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/timetracking/dashboard/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };

  const carregarAjustesPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/timetracking/ajustes/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAjustesPendentes(response.data);
    } catch (error) {
      console.error('Erro ao carregar ajustes:', error);
    }
  };
  
  // NOVO: Carregar ajustes confirmados (aprovados e rejeitados)
  const carregarAjustesConfirmados = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Buscar ajustes de correção aprovados/rejeitados
      const ajustesResp = await axios.get(`${API}/api/timetracking/ajustes/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAjustesConfirmados(ajustesResp.data);
      
      // Buscar pontos esquecidos aprovados/rejeitados
      const pontosResp = await axios.get(`${API}/api/timetracking/solicitacoes-ponto-esquecido?status=aprovado,reprovado`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPontosConfirmados(pontosResp.data);
      
      // Buscar abonos aprovados/rejeitados
      const abonosResp = await axios.get(`${API}/api/abonos?status=APROVADO,REJEITADO`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbonosConfirmados(abonosResp.data);
      
    } catch (error) {
      console.error('Erro ao carregar ajustes confirmados:', error);
    }
  };

  const carregarFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data);
      markTabAsLoaded('funcionarios');
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const carregarConfiguracoes = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Carregar geofences
      const geofencesRes = await axios.get(`${API}/api/timetracking/geofences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeofences(geofencesRes.data);

      // Carregar horários
      const horariosRes = await axios.get(`${API}/api/timetracking/horarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHorarios(horariosRes.data);
      markTabAsLoaded('configuracoes');
      
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const aprovarAjuste = async (ajusteId, aprovado, observacoes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/timetracking/ajustes/${ajusteId}/aprovar?aprovado=${aprovado}&observacoes=${observacoes}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ title: "Sucesso!", description: `Ajuste ${aprovado ? 'aprovado' : 'rejeitado'} com sucesso!`, variant: "default" });
      carregarAjustesPendentes(); // Recarregar lista pendentes
      carregarAjustesConfirmados(); // Recarregar lista confirmados
    } catch (error) {
      console.error('Erro ao processar ajuste:', error);
      toast({ title: "Erro", description: 'Erro ao processar ajuste', variant: "destructive" });
    }
  };

  const rejeitarAjuste = async (ajusteId) => {
    const observacoes = prompt('Motivo da rejeição:');
    if (!observacoes) return;
    
    await aprovarAjuste(ajusteId, false, observacoes);
  };


  const carregarDocumentosEnviados = async (options = {}) => {
    console.log('🔍 Carregando documentos enviados...');
    setCarregandoDocumentos(true);
    try {
      const token = localStorage.getItem('token');
      const statusDoc = options.filtroStatusDoc ?? filtroStatusDoc;
      const dataInicio = options.filtroDataInicio ?? filtroDataInicio;
      const dataFim = options.filtroDataFim ?? filtroDataFim;
      const funcionarioId = options.filtroFuncionarioId ?? filtroFuncionarioId;
      const tipoDocumento = options.filtroTipoDocumento ?? filtroTipoDocumento;
      const page = options.page ?? documentosPagination.page;
      const perPage = options.per_page ?? documentosPagination.per_page;
      
      // Construir URL com filtros
      const params = new URLSearchParams();
      if (statusDoc) params.append('status', statusDoc);
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      if (funcionarioId) params.append('funcionario_id', funcionarioId);
      if (tipoDocumento) params.append('tipo_documento_id', tipoDocumento);
      params.append('page', String(page));
      params.append('per_page', String(perPage));
      params.append('exclude_categoria_rh', 'admissao,demissao,advertencia');

      const url = `${API}/api/documentos/admin${params.toString() ? '?' + params.toString() : ''}`;
      
      console.log('📡 URL:', url);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Documentos recebidos:', response.data);
      setDocumentosEnviados(response.data?.data || []);
      setDocumentosResumo(response.data?.summary || { total: 0, pendente: 0, visualizado: 0, aprovado: 0, reprovado: 0 });
      setDocumentosPagination((prev) => ({
        ...prev,
        ...(response.data?.pagination || {}),
      }));
    } catch (error) {
      console.error('❌ Erro ao carregar documentos:', error);
      console.error('Detalhes:', error.response?.data);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os documentos",
        variant: "destructive"
      });
    } finally {
      setCarregandoDocumentos(false);
    }
  };
  
  const carregarTiposDocumentos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tipos-documentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposDocumentos(response.data);
    } catch (error) {
      console.error('Erro ao carregar tipos de documentos:', error);
    }
  };
  
  const limparFiltrosDocumentos = () => {
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setFiltroFuncionarioId('');
    setFiltroFuncionarioLabel('');
    setFiltroTipoDocumento('');
    setFiltroStatusDoc('');
    setDocumentosPagination((prev) => ({ ...prev, page: 1 }));
  };

  const aplicarFiltrosDocumentos = () => {
    if (documentosPagination.page !== 1) {
      setDocumentosPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    carregarDocumentosEnviados();
  };

  const getObservacaoDocumento = (documentoId) => {
    if (Object.prototype.hasOwnProperty.call(observacoesDoc, documentoId)) {
      return observacoesDoc[documentoId];
    }
    const doc = documentosEnviados.find((item) => item.id === documentoId);
    return doc?.observacoes_rh || '';
  };

  async function ensureDocumentPreview(doc) {
    const previewKind = getDocumentoPreviewKind(doc);
    if (previewKind === 'other' || documentPreviews[doc.id] || documentPreviewLoading[doc.id]) return;

    setDocumentPreviewLoading((prev) => ({ ...prev, [doc.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/documentos/download/${doc.id}?mark_viewed=false`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const contentType = response.headers['content-type'] || response.data.type || doc.arquivo_tipo || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const nextPreview = {
        url,
        kind: previewKind,
        contentType
      };

      if (previewUrlsRef.current[doc.id]?.url) {
        URL.revokeObjectURL(previewUrlsRef.current[doc.id].url);
      }
      previewUrlsRef.current[doc.id] = nextPreview;
      setDocumentPreviews((prev) => ({ ...prev, [doc.id]: nextPreview }));
    } catch (error) {
      console.error('Erro ao carregar preview do documento:', doc.id, error);
    } finally {
      setDocumentPreviewLoading((prev) => ({ ...prev, [doc.id]: false }));
    }
  }

  const handleDownloadDocumentoAdmin = async (documentoId, nomeArquivo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/documentos/download/${documentoId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      // Recarregar para atualizar status
      carregarDocumentosEnviados();
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível baixar o documento",
        variant: "destructive"
      });
    }
  };

  const handleVisualizarDocumentoAnexo = async (documentoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/documentos/download/${documentoId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const contentType = response.headers['content-type'] || response.data.type || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível abrir o documento anexado",
        variant: "destructive"
      });
    }
  };

  const handleSalvarObservacoes = async (documentoId) => {
    const observacao = getObservacaoDocumento(documentoId).trim();
    if (!observacao) {
      toast({
        title: "Campo vazio",
        description: "Digite uma observação antes de salvar",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('observacoes', observacao);

      await axios.put(
        `${API}/api/documentos/${documentoId}/observacoes`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({
        title: "Observação salva!",
        description: "A observação foi adicionada ao documento",
      });

      // Limpar campo e recarregar
      setObservacoesDoc({...observacoesDoc, [documentoId]: ''});
      carregarDocumentosEnviados();
    } catch (error) {
      console.error('Erro ao salvar observação:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a observação",
        variant: "destructive"
      });
    }
  };

  const handleAprovarReprovar = async (documentoId, aprovado) => {
    const status = aprovado ? 'aprovado' : 'reprovado';
    const observacao = getObservacaoDocumento(documentoId).trim();

    if (!aprovado && !observacao) {
      toast({
        title: "Observação obrigatória",
        description: "Digite uma observação antes de reprovar o documento",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('status', status);
      if (observacao) {
        formData.append('observacoes', observacao);
      }

      await axios.put(
        `${API}/api/documentos/${documentoId}/status`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({
        title: aprovado ? "Documento aprovado!" : "Documento reprovado",
        description: `O documento foi ${status} com sucesso`,
      });

      setObservacoesDoc({...observacoesDoc, [documentoId]: ''});
      carregarDocumentosEnviados();
    } catch (error) {
      console.error('Erro ao avaliar documento:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível avaliar o documento",
        variant: "destructive"
      });
    }
  };

  const carregarSolicitacoesAjuste = async () => {
    // Placeholder function for future implementation
    console.log('carregarSolicitacoesAjuste called');
  };
  // Carregar solicitações de ponto esquecido
  const carregarPontosEsquecidos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/timetracking/solicitacoes-ponto-esquecido?status=pendente`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPontosEsquecidos(response.data);
    } catch (error) {
      console.error('Erro ao carregar pontos esquecidos:', error);
    }
  };

  // NOVO: Carregar abonos
  const carregarAbonos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/abonos?status=PENDENTE`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAbonos(response.data);
    } catch (error) {
      console.error('Erro ao carregar abonos:', error);
    }
  };

  // NOVO: Aprovar abono
  const aprovarAbono = async (abonoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/abonos/${abonoId}/aprovar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ 
        title: "Sucesso!", 
        description: "Abono aprovado com sucesso",
      });
      
      carregarAbonos();
      carregarAjustesConfirmados();
    } catch (error) {
      console.error('Erro ao aprovar abono:', error);
      toast({ title: "Erro", description: error.response?.data?.detail || 'Erro ao aprovar abono', variant: "destructive" });
    }
  };

  // NOVO: Rejeitar abono
  const rejeitarAbono = async (abonoId) => {
    const observacoes = prompt('Motivo da rejeição:');
    if (!observacoes) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/abonos/${abonoId}/rejeitar`,
        { observacoes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ 
        title: "Sucesso!", 
        description: "Abono rejeitado",
      });
      
      carregarAbonos();
      carregarAjustesConfirmados();
    } catch (error) {
      console.error('Erro ao rejeitar abono:', error);
      toast({ title: "Erro", description: 'Erro ao rejeitar abono', variant: "destructive" });
    }
  };

  // Aprovar/Reprovar ponto esquecido
  const aprovarPontoEsquecido = async (solicitacaoId, aprovado, observacoes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/timetracking/solicitacoes-ponto-esquecido/${solicitacaoId}/aprovar?aprovado=${aprovado}${observacoes ? `&observacoes=${encodeURIComponent(observacoes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({ 
        title: "Sucesso!", 
        description: `Solicitação ${aprovado ? 'aprovada' : 'reprovada'} com sucesso`,
      });
      
      carregarPontosEsquecidos(); // Recarregar lista pendentes
      carregarAjustesConfirmados(); // Recarregar lista confirmados
    } catch (error) {
      console.error('Erro ao processar solicitação:', error);
      toast({ title: "Erro", description: 'Erro ao processar solicitação', variant: "destructive" });
    }
  };


  // Verificar se tem permissão para Admin - Ponto (granular ou is_admin)
  const hasAdminPontoPermission = user?.is_admin || (
    user?.granular_permissions?.find(m => m.module_id === 'funcionarios')
      ?.tabs?.find(t => t.id === 'ponto_admin')?.enabled
  );

  const documentosStatusResumo = documentosResumo;

  const totalFiltrosDocumentosAtivos = [
    filtroStatusDoc,
    filtroDataInicio,
    filtroDataFim,
    filtroFuncionarioId,
    filtroTipoDocumento
  ].filter(Boolean).length;

  const todasSolicitacoesPendentes = [
    ...pontosEsquecidos.map((s) => ({ ...s, _tipo: 'ponto_esquecido' })),
    ...ajustesPendentes.map((a) => ({ ...a, _tipo: 'correcao' })),
    ...abonos.filter((a) => a.tipo_abono === 'ABONO_FALTA').map((a) => ({ ...a, _tipo: 'abono_falta' })),
    ...abonos.filter((a) => a.tipo_abono === 'BANCO_HORAS').map((a) => ({ ...a, _tipo: 'banco_horas' })),
  ];

  const solicitacoesPendentesFiltradas = filtroTipoSolicitacao === 'todos'
    ? todasSolicitacoesPendentes
    : todasSolicitacoesPendentes.filter((s) => s._tipo === filtroTipoSolicitacao);

  const todasSolicitacoesConfirmadas = [
    ...pontosConfirmados.map((s) => ({ ...s, _tipo: 'ponto_esquecido' })),
    ...ajustesConfirmados.map((a) => ({ ...a, _tipo: 'correcao' })),
    ...abonosConfirmados.filter((a) => a.tipo_abono === 'ABONO_FALTA').map((a) => ({ ...a, _tipo: 'abono_falta' })),
    ...abonosConfirmados.filter((a) => a.tipo_abono === 'BANCO_HORAS').map((a) => ({ ...a, _tipo: 'banco_horas' })),
  ]
    .sort((a, b) => new Date(b.aprovado_em || b.atualizado_em || b.criado_em) - new Date(a.aprovado_em || a.atualizado_em || a.criado_em));

  const solicitacoesConfirmadasFiltradas = filtroTipoSolicitacao === 'todos'
    ? todasSolicitacoesConfirmadas
    : todasSolicitacoesConfirmadas.filter((s) => s._tipo === filtroTipoSolicitacao);

  const ajustesResumo = {
    pendentes: todasSolicitacoesPendentes.length,
    confirmados: todasSolicitacoesConfirmadas.length,
    ponto_esquecido: todasSolicitacoesPendentes.filter((s) => s._tipo === 'ponto_esquecido').length,
    correcao: todasSolicitacoesPendentes.filter((s) => s._tipo === 'correcao').length,
    abono_falta: todasSolicitacoesPendentes.filter((s) => s._tipo === 'abono_falta').length,
    banco_horas: todasSolicitacoesPendentes.filter((s) => s._tipo === 'banco_horas').length,
  };

  if (!hasAdminPontoPermission) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-12">
            <Shield className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold mb-2">Acesso Restrito</h2>
            <p className="text-gray-600">Você não tem permissão para acessar esta área.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Administração - Sistema de Ponto</h1>
          <p className="text-gray-600 mt-1">Painel administrativo REP-P - Portaria 671/2021</p>
        </div>
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-green-600" />
          <span className="text-sm text-green-600 font-medium">Admin Dashboard</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => {
        console.log('📌 Tabs onValueChange chamado. Novo valor:', value);
        setActiveTab(value);
      }} className="space-y-6">
        <TabsList
          className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:overflow-x-visible"
          style={{gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))`}}
        >
          {visibleTabs.includes('dashboard') && (
            <TabsTrigger value="dashboard" className="flex flex-shrink-0 items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Dashboard</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('ajustes') && (
            <TabsTrigger value="ajustes" className="flex flex-shrink-0 items-center space-x-2">
              <Edit className="h-4 w-4" />
              <span>Ajustes</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('documentos-enviados') && (
            <TabsTrigger value="documentos-enviados" className="flex flex-shrink-0 items-center space-x-2">
            <FileCheck className="h-4 w-4" />
            <span>Documentos</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('funcionarios') && (
            <TabsTrigger value="funcionarios" className="flex flex-shrink-0 items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Funcionários</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('afd') && (
            <TabsTrigger value="afd" className="flex flex-shrink-0 items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>AFD</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('aej') && (
            <TabsTrigger value="aej" className="flex flex-shrink-0 items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>AEJ</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('configuracoes') && (
            <TabsTrigger value="configuracoes" className="flex flex-shrink-0 items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Config</span>
            </TabsTrigger>
          )}
          {visibleTabs.includes('relatorios') && (
            <TabsTrigger value="relatorios" className="flex flex-shrink-0 items-center space-x-2">
              <Activity className="h-4 w-4" />
              <span>Relatórios</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Aba Dashboard */}
        {allowedTabs.includes('dashboard') && (
        <TabsContent value="dashboard">
          <DashboardOverview 
            data={dashboardData} 
            onRefresh={carregarDashboard}
            loading={loading}
          />
        </TabsContent>
        )}

        {/* Aba Ajustes UNIFICADA */}
        {allowedTabs.includes('ajustes') && (
        <TabsContent value="ajustes" className="space-y-6">
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-violet-50 via-white to-blue-50">
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <Clock className="h-5 w-5 text-violet-600" />
                Gerenciamento de Ajustes
              </CardTitle>
              <CardDescription className="text-slate-600">
                Solicitações de ponto, correções e abonos com triagem visual no mesmo padrão dos documentos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Card className="border-violet-100 bg-violet-50/70 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-violet-700">Pendentes</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{ajustesResumo.pendentes}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-violet-500" />
                  </CardContent>
                </Card>
                <Card className="border-blue-100 bg-blue-50/70 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-blue-700">Correções</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{ajustesResumo.correcao}</p>
                    </div>
                    <Edit className="h-8 w-8 text-blue-500" />
                  </CardContent>
                </Card>
                <Card className="border-orange-100 bg-orange-50/70 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-orange-700">Abonos</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{ajustesResumo.abono_falta}</p>
                    </div>
                    <FileCheck className="h-8 w-8 text-orange-500" />
                  </CardContent>
                </Card>
                <Card className="border-emerald-100 bg-emerald-50/70 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Processados</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{ajustesResumo.confirmados}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                  </CardContent>
                </Card>
              </div>

              <Tabs value={abaAjustes} onValueChange={setAbaAjustes}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="pendentes" className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Ajustes Pendentes</span>
                    {ajustesResumo.pendentes > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {ajustesResumo.pendentes}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="confirmados" className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>Histórico de Ajustes</span>
                  </TabsTrigger>
                </TabsList>

                {/* ABA PENDENTES */}
                <TabsContent value="pendentes" className="space-y-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Solicitações Pendentes de Aprovação</h3>
                      <p className="text-sm text-slate-500">Triagem operacional das solicitações recebidas pelos colaboradores.</p>
                    </div>
                    <select
                      value={filtroTipoSolicitacao}
                      onChange={(e) => setFiltroTipoSolicitacao(e.target.value)}
                      className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                    >
                      <option value="todos">Todos os Tipos</option>
                      <option value="ponto_esquecido">Ponto Esquecido</option>
                      <option value="correcao">Correção de Registro</option>
                      <option value="abono_falta">Abono de Falta</option>
                      <option value="banco_horas">Banco de Horas</option>
                    </select>
                  </div>
                  {solicitacoesPendentesFiltradas.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma solicitação pendente</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
                      {solicitacoesPendentesFiltradas.map((sol) => {
                        const tipoConfig = getSolicitacaoTipoConfig(sol._tipo);
                        return (
                          <Card key={sol.id} className="overflow-hidden border-slate-200 shadow-sm">
                            <div className={`border-b p-4 ${tipoConfig.surfaceClass}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <h3 className="truncate text-lg font-semibold text-slate-900">{sol.funcionario_nome}</h3>
                                  <p className="text-sm text-slate-600">{sol.funcionario_cpf || sol.funcionario_id}</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <Badge className={tipoConfig.badgeClass}>{tipoConfig.label}</Badge>
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pendente</Badge>
                                </div>
                              </div>
                            </div>
                            <CardContent className="space-y-4 p-5">
                              {sol._tipo === 'ponto_esquecido' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.data.split('-').reverse().join('/')}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Hora</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.hora}</p>
                                  </div>
                                  <div className="col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Tipo de ponto</p>
                                    <p className="mt-1 text-sm font-medium capitalize text-slate-800">{sol.tipo_ponto.replace('_', ' ')}</p>
                                  </div>
                                </div>
                              )}

                              {sol._tipo === 'correcao' && (
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{formatarDataCurta(sol.data_hora_nova)}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Tipo</p>
                                    <p className="mt-1 text-sm font-medium capitalize text-slate-800">{sol.tipo_marcacao?.replace('_', ' ') || 'N/A'}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Horário</p>
                                    <p className="mt-1 text-sm font-medium text-blue-700">
                                      {sol.data_hora_original && <span className="mr-2 text-slate-400 line-through">{formatarHoraCurta(sol.data_hora_original)}</span>}
                                      {formatarHoraCurta(sol.data_hora_nova)}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {sol._tipo === 'abono_falta' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Período</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">
                                      {sol.data_inicio.split('-').reverse().join('/')}
                                      {sol.data_fim !== sol.data_inicio && ` a ${sol.data_fim.split('-').reverse().join('/')}`}
                                    </p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Horas</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.horas_abonadas ? `${sol.horas_abonadas}h` : 'Dia inteiro'}</p>
                                  </div>
                                  {sol.justificativa_nome && (
                                    <div className="col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Motivo cadastrado</p>
                                      <p className="mt-1 text-sm font-medium text-slate-800">{sol.justificativa_nome}</p>
                                    </div>
                                  )}
                                  {sol.documento_id && (
                                    <Button variant="outline" className="col-span-2 justify-start" onClick={() => handleVisualizarDocumentoAnexo(sol.documento_id)}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      Visualizar documento anexo
                                    </Button>
                                  )}
                                </div>
                              )}

                              {sol._tipo === 'banco_horas' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.data_inicio.split('-').reverse().join('/')}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Horas</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.horas_abonadas}h</p>
                                  </div>
                                </div>
                              )}

                              {(sol.justificativa || sol.justificativa_texto) && (
                                <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-sky-700">Justificativa</p>
                                  <p className="mt-1 text-sm text-sky-900">{sol.justificativa || sol.justificativa_texto}</p>
                                </div>
                              )}

                              {sol.detalhes && (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Mais detalhes</p>
                                  <p className="mt-1 text-sm text-slate-700">{sol.detalhes}</p>
                                </div>
                              )}

                              <p className="text-xs text-slate-500">Solicitado em {formatarDataHora(sol.criado_em || sol.solicitado_em)}</p>

                              <div className="grid grid-cols-2 gap-2">
                                {sol._tipo === 'ponto_esquecido' ? (
                                  <>
                                    <Button onClick={() => aprovarPontoEsquecido(sol.id, true)} className="bg-emerald-600 hover:bg-emerald-700">
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Aprovar
                                    </Button>
                                    <Button onClick={() => aprovarPontoEsquecido(sol.id, false, 'Solicitação reprovada')} className="bg-rose-600 hover:bg-rose-700">
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Reprovar
                                    </Button>
                                  </>
                                ) : sol._tipo === 'correcao' ? (
                                  <>
                                    <Button onClick={() => aprovarAjuste(sol.id, true, '')} className="bg-emerald-600 hover:bg-emerald-700">
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Aprovar
                                    </Button>
                                    <Button onClick={() => rejeitarAjuste(sol.id)} className="bg-rose-600 hover:bg-rose-700">
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Reprovar
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button onClick={() => aprovarAbono(sol.id)} className="bg-emerald-600 hover:bg-emerald-700">
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Aprovar
                                    </Button>
                                    <Button onClick={() => rejeitarAbono(sol.id)} className="bg-rose-600 hover:bg-rose-700">
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Reprovar
                                    </Button>
                                  </>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* ABA CONFIRMADOS (HISTÓRICO) */}
                <TabsContent value="confirmados" className="space-y-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Histórico de Ajustes Processados</h3>
                      <p className="text-sm text-slate-500">Solicitações já avaliadas, com decisão e trilha operacional.</p>
                    </div>
                    <select
                      value={filtroTipoSolicitacao}
                      onChange={(e) => setFiltroTipoSolicitacao(e.target.value)}
                      className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                    >
                      <option value="todos">Todos os Tipos</option>
                      <option value="ponto_esquecido">Ponto Esquecido</option>
                      <option value="correcao">Correção de Registro</option>
                      <option value="abono_falta">Abono de Falta</option>
                      <option value="banco_horas">Banco de Horas</option>
                    </select>
                  </div>

                  {solicitacoesConfirmadasFiltradas.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhum ajuste processado ainda</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
                      {solicitacoesConfirmadasFiltradas.map((sol) => {
                        const tipoConfig = getSolicitacaoTipoConfig(sol._tipo);
                        const foiAprovado = sol.status?.toUpperCase() === 'APROVADO' || sol.aprovado === true;
                        return (
                          <Card key={sol.id} className="overflow-hidden border-slate-200 shadow-sm">
                            <div className={`border-b p-4 ${foiAprovado ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <h3 className="truncate text-lg font-semibold text-slate-900">{sol.funcionario_nome}</h3>
                                  <p className="text-sm text-slate-600">{sol.funcionario_cpf || sol.funcionario_id}</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <Badge className={tipoConfig.badgeClass}>{tipoConfig.label}</Badge>
                                  <Badge className={foiAprovado ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-rose-100 text-rose-800 border-rose-200'}>
                                    {foiAprovado ? 'Aprovado' : 'Rejeitado'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <CardContent className="space-y-4 p-5">
                              {sol._tipo === 'ponto_esquecido' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.data.split('-').reverse().join('/')}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Hora</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.hora}</p>
                                  </div>
                                </div>
                              )}

                              {sol._tipo === 'correcao' && (
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{formatarDataCurta(sol.data_hora_nova)}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Tipo</p>
                                    <p className="mt-1 text-sm font-medium capitalize text-slate-800">{sol.tipo_marcacao?.replace('_', ' ') || 'N/A'}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Horário</p>
                                    <p className="mt-1 text-sm font-medium text-blue-700">
                                      {sol.data_hora_original && <span className="mr-2 text-slate-400 line-through">{formatarHoraCurta(sol.data_hora_original)}</span>}
                                      {formatarHoraCurta(sol.data_hora_nova)}
                                    </p>
                                  </div>
                                </div>
                              )}

                              {sol._tipo === 'abono_falta' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Período</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">
                                      {sol.data_inicio.split('-').reverse().join('/')}
                                      {sol.data_fim !== sol.data_inicio && ` a ${sol.data_fim.split('-').reverse().join('/')}`}
                                    </p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Horas</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.horas_abonadas ? `${sol.horas_abonadas}h` : 'Dia inteiro'}</p>
                                  </div>
                                </div>
                              )}

                              {sol._tipo === 'banco_horas' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Data</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.data_inicio.split('-').reverse().join('/')}</p>
                                  </div>
                                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Horas</p>
                                    <p className="mt-1 text-sm font-medium text-slate-800">{sol.horas_abonadas}h</p>
                                  </div>
                                </div>
                              )}

                              {(sol.justificativa || sol.justificativa_texto) && (
                                <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-sky-700">Justificativa</p>
                                  <p className="mt-1 text-sm text-sky-900">{sol.justificativa || sol.justificativa_texto}</p>
                                </div>
                              )}

                              {sol.detalhes && (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Mais detalhes</p>
                                  <p className="mt-1 text-sm text-slate-700">{sol.detalhes}</p>
                                </div>
                              )}

                              {sol.observacoes_aprovacao && (
                                <div className={`rounded-xl border p-3 ${foiAprovado ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
                                  <p className={`text-[11px] font-medium uppercase tracking-wide ${foiAprovado ? 'text-emerald-700' : 'text-rose-700'}`}>Observações do RH</p>
                                  <p className={`mt-1 text-sm ${foiAprovado ? 'text-emerald-900' : 'text-rose-900'}`}>{sol.observacoes_aprovacao}</p>
                                </div>
                              )}

                              {sol.documento_id && (
                                <Button variant="outline" className="w-full justify-start" onClick={() => handleVisualizarDocumentoAnexo(sol.documento_id)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Visualizar documento anexo
                                </Button>
                              )}

                              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                                <p>Solicitado em {formatarDataHora(sol.criado_em || sol.solicitado_em)}</p>
                                {sol.aprovado_por && <p className="mt-1">Processado por {sol.aprovado_por} em {formatarDataHora(sol.aprovado_em)}</p>}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Aba Documentos Enviados */}
        <TabsContent value="documentos-enviados">
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-orange-50 via-white to-amber-50">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-2">
                  <CardTitle className="flex items-center gap-2 text-slate-900">
                    <FileCheck className="h-5 w-5 text-orange-600" />
                    Documentos Enviados pelos Colaboradores
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    Central de triagem com preview, avaliação e observações do RH no mesmo fluxo.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '', label: 'Todos', count: documentosStatusResumo.total },
                    { value: 'pendente', label: 'Pendentes', count: documentosStatusResumo.pendente },
                    { value: 'visualizado', label: 'Visualizados', count: documentosStatusResumo.visualizado },
                    { value: 'aprovado', label: 'Aprovados', count: documentosStatusResumo.aprovado },
                    { value: 'reprovado', label: 'Reprovados', count: documentosStatusResumo.reprovado },
                  ].map((item) => (
                    <Button
                      key={item.value || 'todos'}
                      variant={filtroStatusDoc === item.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setFiltroStatusDoc(item.value);
                        setDocumentosPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      className={filtroStatusDoc === item.value ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-200 text-orange-700 hover:bg-orange-50'}
                    >
                      {item.label} ({item.count})
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6 pt-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Card className="border-orange-100 bg-orange-50/70 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-orange-700">Total</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{documentosStatusResumo.total}</p>
                    </div>
                    <FileText className="h-8 w-8 text-orange-500" />
                  </CardContent>
                </Card>
                <Card className="border-amber-100 bg-amber-50/80 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Pendentes</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{documentosStatusResumo.pendente}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                  </CardContent>
                </Card>
                <Card className="border-sky-100 bg-sky-50/80 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-sky-700">Visualizados</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{documentosStatusResumo.visualizado}</p>
                    </div>
                    <Eye className="h-8 w-8 text-sky-500" />
                  </CardContent>
                </Card>
                <Card className="border-emerald-100 bg-emerald-50/80 shadow-none">
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Concluídos</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">
                        {documentosStatusResumo.aprovado + documentosStatusResumo.reprovado}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                  </CardContent>
                </Card>
              </div>

              <Card className="border-slate-200 bg-slate-50/70 shadow-none">
                <CardContent className="pt-6">
                  <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Filtros de triagem</h3>
                      <p className="text-sm text-slate-500">
                        {totalFiltrosDocumentosAtivos > 0
                          ? `${totalFiltrosDocumentosAtivos} filtro(s) ativo(s)`
                          : 'Nenhum filtro ativo'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={aplicarFiltrosDocumentos}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        Aplicar filtros
                      </Button>
                      <Button
                        onClick={() => {
                          limparFiltrosDocumentos();
                          carregarDocumentosEnviados({
                            filtroStatusDoc: '',
                            filtroDataInicio: '',
                            filtroDataFim: '',
                            filtroFuncionarioId: '',
                            filtroTipoDocumento: '',
                            page: 1,
                          });
                        }}
                        variant="outline"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Limpar
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <Label htmlFor="filtro-data-inicio" className="text-sm font-medium mb-1 block">
                        Data Início
                      </Label>
                      <Input
                        id="filtro-data-inicio"
                        type="date"
                        value={filtroDataInicio}
                        onChange={(e) => setFiltroDataInicio(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label htmlFor="filtro-data-fim" className="text-sm font-medium mb-1 block">
                        Data Fim
                      </Label>
                      <Input
                        id="filtro-data-fim"
                        type="date"
                        value={filtroDataFim}
                        onChange={(e) => setFiltroDataFim(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Label htmlFor="filtro-funcionario" className="text-sm font-medium mb-1 block">
                        Funcionário
                      </Label>
                      <Autocomplete
                        value={filtroFuncionarioId}
                        onValueChange={(value, item) => {
                          setFiltroFuncionarioId(value);
                          setFiltroFuncionarioLabel(item ? `${item.nome || item.name}${item.cargo ? ` - ${item.cargo}` : ''}` : '');
                        }}
                        endpoint={`${API}/api/employees-paged`}
                        searchParam="search"
                        getOptionValue={(item) => item.id}
                        getOptionLabel={(item) => `${item.nome || item.name}${item.cargo ? ` - ${item.cargo}` : ''}`}
                        placeholder="Buscar colaborador"
                        selectedLabel={filtroFuncionarioLabel}
                        minChars={1}
                        limit={12}
                        extraParams={{ active_only: true }}
                        emptyMessage="Nenhum colaborador encontrado"
                      />
                    </div>
                    <div>
                      <Label htmlFor="filtro-tipo-doc" className="text-sm font-medium mb-1 block">
                        Tipo de Documento
                      </Label>
                      <select
                        id="filtro-tipo-doc"
                        value={filtroTipoDocumento}
                        onChange={(e) => setFiltroTipoDocumento(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Todos os tipos</option>
                        {tiposDocumentos.filter(tipo => tipo.ativo).map((tipo) => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {carregandoDocumentos ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-12 w-12 mx-auto animate-spin text-orange-600 mb-4" />
                  <p className="text-gray-600">Carregando documentos...</p>
                </div>
              ) : documentosEnviados.length === 0 ? (
                <div className="text-center py-12">
                  <FileCheck className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600 text-lg mb-2">
                    {filtroStatusDoc ? `Nenhum documento ${filtroStatusDoc}` : 'Nenhum documento enviado'}
                  </p>
                  <p className="text-gray-500 text-sm">
                    Os documentos enviados pelos colaboradores aparecerão aqui
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 2xl:grid-cols-3">
                  {documentosEnviados.map((doc) => (
                    <Card key={doc.id} className="overflow-hidden border-slate-200 shadow-sm">
                      <div className="relative aspect-[16/10] border-b bg-slate-100">
                        <div className="absolute left-3 top-3 z-10">
                          <Badge className={getDocumentoStatusConfig(doc.status).badgeClass}>
                            {getDocumentoStatusConfig(doc.status).label}
                          </Badge>
                        </div>

                        {documentPreviewLoading[doc.id] ? (
                          <div className="flex h-full items-center justify-center">
                            <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
                          </div>
                        ) : documentPreviews[doc.id]?.kind === 'image' ? (
                          <img
                            src={documentPreviews[doc.id].url}
                            alt={doc.arquivo_nome}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : documentPreviews[doc.id]?.kind === 'pdf' ? (
                          <iframe
                            src={documentPreviews[doc.id].url}
                            title={`Preview ${doc.arquivo_nome}`}
                            className="h-full w-full bg-white"
                          />
                        ) : (
                          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                            <FileText className="h-10 w-10 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-700">Preview indisponível</p>
                              <p className="text-sm text-slate-500">Use “Visualizar” para abrir o arquivo completo.</p>
                            </div>
                          </div>
                        )}

                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/80 to-transparent p-4">
                          <p className="truncate text-sm font-medium text-white">{doc.arquivo_nome}</p>
                          <p className="text-xs text-slate-200">{formatarTamanhoArquivo(doc.arquivo_size)}</p>
                        </div>
                      </div>

                      <CardContent className="space-y-4 p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="truncate text-lg font-semibold text-slate-900">{doc.tipo_documento_nome}</h3>
                            <p className="mt-1 text-sm text-slate-600">
                              <span className="font-medium">Colaborador:</span> {doc.funcionario_nome}
                            </p>
                          </div>
                          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                            {doc.categoria_rh || doc.document_category || 'geral'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Enviado em</p>
                            <p className="mt-1 text-sm font-medium text-slate-800">{formatarDataHora(doc.criado_em)}</p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Arquivo</p>
                            <p className="mt-1 truncate text-sm font-medium text-slate-800">{doc.arquivo_tipo || 'Tipo não informado'}</p>
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Motivo</p>
                          <p className="mt-1 text-sm text-slate-700">{doc.motivo || 'Sem motivo informado'}</p>
                        </div>

                        {doc.visualizado_em && (
                          <div className="rounded-xl border border-sky-100 bg-sky-50 p-3 text-sm text-sky-900">
                            Visualizado em {formatarDataHora(doc.visualizado_em)}
                            {doc.visualizado_por ? ` por ${doc.visualizado_por}` : ''}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            onClick={() => handleVisualizarDocumentoAnexo(doc.id)}
                            className="border-slate-300"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizar
                          </Button>
                          <Button
                            onClick={() => handleDownloadDocumentoAdmin(doc.id, doc.arquivo_nome)}
                            className="bg-orange-600 hover:bg-orange-700"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Baixar
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-slate-700">Observações do RH</Label>
                          <Textarea
                            value={getObservacaoDocumento(doc.id)}
                            onChange={(e) => setObservacoesDoc({
                              ...observacoesDoc,
                              [doc.id]: e.target.value
                            })}
                            placeholder="Digite observações sobre este documento..."
                            rows={4}
                          />
                          <Button
                            onClick={() => handleSalvarObservacoes(doc.id)}
                            variant="outline"
                            className="w-full"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Salvar observações
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            onClick={() => handleAprovarReprovar(doc.id, true)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {doc.status === 'aprovado' ? 'Aprovado' : 'Aprovar'}
                          </Button>
                          <Button
                            onClick={() => handleAprovarReprovar(doc.id, false)}
                            className="bg-rose-600 hover:bg-rose-700"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            {doc.status === 'reprovado' ? 'Reprovado' : 'Reprovar'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {!carregandoDocumentos && documentosPagination.total > 0 && (
                <div className="flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between">
                  <p className="text-sm text-slate-500">
                    Mostrando {documentosEnviados.length} de {documentosPagination.total} documento(s)
                    {' '}na página {documentosPagination.page} de {documentosPagination.total_pages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDocumentosPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={!documentosPagination.has_prev}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDocumentosPagination((prev) => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
                      disabled={!documentosPagination.has_next}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Aba Funcionários */}
        {allowedTabs.includes('funcionarios') && (
        <TabsContent value="funcionarios">
          <GestaoFuncionarios 
            funcionarios={funcionarios}
            filtros={filtros}
            setFiltros={setFiltros}
            onRefresh={carregarFuncionarios}
          />
        </TabsContent>
        )}

        {/* Aba Configurações */}
        {allowedTabs.includes('configuracoes') && (
        <TabsContent value="configuracoes">
          <ConfiguracoesAdmin 
            geofences={geofences}
            horarios={horarios}
            onRefresh={carregarConfiguracoes}
          />
        </TabsContent>
        )}

        {/* Aba Relatórios */}
        {allowedTabs.includes('relatorios') && (
        <TabsContent value="relatorios">
          <RelatoriosAdmin 
            funcionarios={funcionarios}
            filtros={filtros}
            setFiltros={setFiltros}
          />
        </TabsContent>
        )}

        {/* Aba AFD */}
        {allowedTabs.includes('afd') && (
        <TabsContent value="afd">
          <GerarAFD />
        </TabsContent>
        )}

        {/* Aba PTRP/AEJ */}
        {allowedTabs.includes('aej') && (
        <TabsContent value="aej">
          <GerarAEJ />
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// Componente Dashboard Overview
const DashboardOverview = ({ data, onRefresh, loading }) => {
  const estatisticas = data.estatisticas || {};
  const ultimos_registros = data.ultimos_registros || [];

  const formatarDataHora = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getTipoMarcacaoBadge = (tipo) => {
    const variants = {
      'ENTRADA': { color: 'bg-green-100 text-green-800', label: 'Entrada' },
      'SAIDA': { color: 'bg-red-100 text-red-800', label: 'Saída' },
      'SAIDA_INTERVALO': { color: 'bg-yellow-100 text-yellow-800', label: 'Saída Intervalo' },
      'RETORNO_INTERVALO': { color: 'bg-blue-100 text-blue-800', label: 'Retorno Intervalo' }
    };
    
    const config = variants[tipo] || { color: 'bg-gray-100 text-gray-800', label: tipo };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Estatísticas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Funcionários</p>
                <p className="text-3xl font-bold">{estatisticas.total_funcionarios || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Registros Hoje</p>
                <p className="text-3xl font-bold">{estatisticas.total_registros_hoje || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ajustes Pendentes</p>
                <p className="text-3xl font-bold text-orange-600">{estatisticas.ajustes_pendentes || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Registros de Pontos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Registros de Pontos</CardTitle>
            <CardDescription>Todas as marcações de ponto realizadas</CardDescription>
          </div>
          <Button onClick={onRefresh} disabled={loading}>
            <Activity className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Activity className="h-8 w-8 mx-auto animate-spin text-blue-600" />
              <p className="text-gray-600 mt-2">Carregando...</p>
            </div>
          ) : ultimos_registros.length > 0 ? (
            <div className="space-y-3">
              {ultimos_registros.map((registro) => (
                <div key={registro.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    {/* Foto do registro */}
                    <FotoRegistro registro={registro} />
                    
                    <div>
                      <div className="font-medium">{registro.funcionario_nome}</div>
                      <div className="text-sm text-gray-600">
                        {formatarDataHora(registro.data_hora)}
                      </div>
                      {/* Geolocalização */}
                      {(registro.latitude && registro.longitude) && (
                        <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{registro.latitude.toFixed(6)}, {registro.longitude.toFixed(6)}</span>
                          <a 
                            href={`https://www.google.com/maps?q=${registro.latitude},${registro.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 underline hover:text-blue-800"
                          >
                            (ver mapa)
                          </a>
                        </div>
                      )}
                    </div>
                    <div>{getTipoMarcacaoBadge(registro.tipo_marcacao)}</div>
                    <div className="text-sm text-gray-500">{registro.origem}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {registro.geofence_valida ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhum registro encontrado</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente Ajustes de Aprovação  
const AjustesAprovacao = ({ ajustes, onAprovar, onRefresh }) => {
  const [ajusteSelecionado, setAjusteSelecionado] = useState(null);
  const [observacoes, setObservacoes] = useState('');

  const formatarDataHora = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const handleAprovar = (ajuste, aprovado) => {
    onAprovar(ajuste.id, aprovado, observacoes);
    setAjusteSelecionado(null);
    setObservacoes('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Aprovação de Ajustes</h2>
          <p className="text-gray-600">{ajustes.length} ajuste(s) aguardando aprovação</p>
        </div>
        <Button onClick={onRefresh}>
          <Activity className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Lista de Ajustes */}
      {ajustes.length > 0 ? (
        <div className="grid gap-4">
          {ajustes.map((ajuste) => (
            <Card key={ajuste.id} className="border-l-4 border-l-orange-400">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    {/* Informações do Funcionário */}
                    <div>
                      <h3 className="font-semibold text-lg">{ajuste.funcionario_nome}</h3>
                      <p className="text-sm text-gray-600">
                        Solicitado em: {formatarDataHora(ajuste.criado_em)}
                      </p>
                    </div>

                    {/* Detalhes do Ajuste */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Tipo de Ajuste</Label>
                        <Badge variant="outline" className="mt-1">{ajuste.tipo_ajuste}</Badge>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Nova Data/Hora</Label>
                        <p className="text-sm">{formatarDataHora(ajuste.data_hora_nova)}</p>
                      </div>
                    </div>

                    {/* Justificativa */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Justificativa</Label>
                      <p className="text-sm bg-gray-50 p-3 rounded-lg mt-1">{ajuste.justificativa}</p>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex flex-col space-y-2 ml-6">
                    <Button 
                      onClick={() => setAjusteSelecionado(ajuste)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Aprovar
                    </Button>
                    <Button 
                      onClick={() => handleAprovar(ajuste, false)}
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Rejeitar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-300" />
            <h3 className="text-xl font-semibold mb-2">Nenhum ajuste pendente</h3>
            <p className="text-gray-600">Todos os ajustes foram processados.</p>
          </CardContent>
        </Card>
      )}

      {/* Modal de Aprovação */}
      {ajusteSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Aprovar Ajuste</h3>
            <p className="text-sm text-gray-600 mb-4">
              Funcionário: <strong>{ajusteSelecionado.funcionario_nome}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="observacoes">Observações (opcional)</Label>
                <Input
                  id="observacoes"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Adicione observações sobre a aprovação..."
                />
              </div>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={() => handleAprovar(ajusteSelecionado, true)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Aprovar
                </Button>
                <Button 
                  onClick={() => setAjusteSelecionado(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente Gestão de Funcionários
const GestaoFuncionarios = ({ funcionarios, filtros, setFiltros, onRefresh }) => {
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  const [registrosFuncionario, setRegistrosFuncionario] = useState([]);
  const [todosRegistros, setTodosRegistros] = useState({}); // Armazena registros de todos os funcionários
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const { toast } = useToast();

  // Carregar registros de todos os funcionários ao montar ou quando filtros mudarem
  useEffect(() => {
    if (funcionarios.length > 0) {
      carregarTodosRegistros();
    }
  }, [filtros.dataInicio, filtros.dataFim, funcionarios.length]);

  const carregarTodosRegistros = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const registrosPorFuncionario = {};
      
      // Carregar registros de cada funcionário
      await Promise.all(
        funcionarios.map(async (func) => {
          try {
            const response = await axios.get(
              `${API}/api/timetracking/registros/${func.id}?data_inicio=${filtros.dataInicio}&data_fim=${filtros.dataFim}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            registrosPorFuncionario[func.id] = response.data.registros || [];
          } catch (error) {
            console.error(`Erro ao carregar registros de ${func.name}:`, error);
            registrosPorFuncionario[func.id] = [];
          }
        })
      );
      
      setTodosRegistros(registrosPorFuncionario);
    } catch (error) {
      console.error('Erro ao carregar registros:', error);
      toast({ title: "Erro", description: 'Erro ao carregar registros dos funcionários', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const carregarRegistrosFuncionario = async (funcionarioId) => {
    setLoadingModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/timetracking/registros/${funcionarioId}?data_inicio=${filtros.dataInicio}&data_fim=${filtros.dataFim}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistrosFuncionario(response.data.registros || []);
    } catch (error) {
      console.error('Erro ao carregar registros:', error);
      toast({ title: "Erro", description: 'Erro ao carregar registros do funcionário', variant: "destructive" });
    } finally {
      setLoadingModal(false);
    }
  };

  const calcularEstatisticasFuncionario = (funcionario) => {
    const registros = todosRegistros[funcionario.id] || [];
    const hoje = new Date().toISOString().split('T')[0];
    const registrosHoje = registros.filter(r => {
      if (!r.data_hora) return false;
      const dataReg = r.data_hora.split('T')[0];
      return dataReg === hoje;
    });
    
    return {
      total_registros: registros.length,
      registros_hoje: registrosHoje.length,
      ultimo_registro: registros[0]?.data_hora ? new Date(registros[0].data_hora).toLocaleString('pt-BR') : 'Nunca'
    };
  };

  const formatarDataHora = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getTipoMarcacaoBadge = (tipo) => {
    const variants = {
      'ENTRADA': { color: 'bg-green-100 text-green-800', label: 'Entrada' },
      'SAIDA': { color: 'bg-red-100 text-red-800', label: 'Saída' },
      'SAIDA_INTERVALO': { color: 'bg-yellow-100 text-yellow-800', label: 'Saída Intervalo' },
      'RETORNO_INTERVALO': { color: 'bg-blue-100 text-blue-800', label: 'Retorno Intervalo' }
    };
    
    const config = variants[tipo] || { color: 'bg-gray-100 text-gray-800', label: tipo };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="data-inicio">Data Início</Label>
              <Input
                id="data-inicio"
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="data-fim">Data Fim</Label>
              <Input
                id="data-fim"
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="funcionario-filtro">Funcionário</Label>
              <select
                id="funcionario-filtro"
                value={filtros.funcionario}
                onChange={(e) => setFiltros(prev => ({ ...prev, funcionario: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="todos">Todos os funcionários</option>
                {funcionarios.map(func => (
                  <option key={func.id} value={func.id}>{func.nome || func.name || `Funcionário ${func.id.substring(0, 8)}`}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={carregarTodosRegistros} className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Activity className="h-4 w-4 mr-2 animate-spin" />
                    Carregando...
                  </>
                ) : (
                  <>
                    <Filter className="h-4 w-4 mr-2" />
                    Aplicar
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Funcionários */}
      {loading ? (
        <Card>
          <CardContent className="text-center py-12">
            <Activity className="h-12 w-12 mx-auto animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600">Carregando registros dos funcionários...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Funcionários ({funcionarios.length})</h2>
          </div>

          <div className="grid gap-4">
          {funcionarios
            .filter(func => filtros.funcionario === 'todos' || func.id === filtros.funcionario)
            .map((funcionario) => {
              const stats = calcularEstatisticasFuncionario(funcionario);
              
              return (
                <Card key={funcionario.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{funcionario.nome || 'Sem nome'}</h3>
                        <p className="text-sm text-gray-600">{funcionario.cargo || 'Cargo não definido'}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span>CPF: {funcionario.cpf || 'N/A'}</span>
                          <span>Email: {funcionario.email || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-xl font-bold text-blue-600">{stats.registros_hoje}</div>
                          <div className="text-xs text-gray-600">Hoje</div>
                        </div>
                        <div>
                          <div className="text-xl font-bold text-purple-600">{stats.total_registros}</div>
                          <div className="text-xs text-gray-600">Total</div>
                        </div>
                        <div>
                          <Button 
                            size="sm" 
                            onClick={() => {
                              setFuncionarioSelecionado(funcionario);
                              carregarRegistrosFuncionario(funcionario.id);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Detalhes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Funcionário */}
      {funcionarioSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Detalhes - {funcionarioSelecionado.nome || 'Sem nome'}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFuncionarioSelecionado(null)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </CardTitle>
              <CardDescription>
                Período: {filtros.dataInicio} até {filtros.dataFim}
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              {loadingModal ? (
                <div className="text-center py-8">
                  <Activity className="h-8 w-8 mx-auto animate-spin text-blue-600" />
                  <p className="text-gray-600 mt-2">Carregando registros...</p>
                </div>
              ) : registrosFuncionario.length > 0 ? (
                <div className="space-y-3">
                  {registrosFuncionario.map((registro) => (
                    <div key={registro.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div>
                            <div className="font-medium">
                              {new Date(registro.data_hora).toLocaleDateString('pt-BR')}
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(registro.data_hora).toLocaleTimeString('pt-BR')}
                            </div>
                            {(registro.latitude && registro.longitude) && (
                              <div className="flex items-center space-x-1 mt-1 text-xs text-blue-600">
                                <MapPin className="h-3 w-3" />
                                <span>
                                  Lat: {Number(registro.latitude).toFixed(6)}, 
                                  Long: {Number(registro.longitude).toFixed(6)}
                                </span>
                                <a 
                                  href={`https://www.google.com/maps?q=${registro.latitude},${registro.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 underline hover:text-blue-800"
                                  title="Abrir no Google Maps"
                                >
                                  (ver mapa)
                                </a>
                              </div>
                            )}
                          </div>
                          <div>{getTipoMarcacaoBadge(registro.tipo_marcacao)}</div>
                          <div className="text-sm text-gray-600">{registro.origem}</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {registro.geofence_valida ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <Badge variant={registro.status === 'VALIDA' ? 'default' : 'secondary'}>
                            {registro.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum registro encontrado para o período selecionado</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const ConfiguracoesAdmin = ({ geofences, horarios, onRefresh }) => {
  const [modalGeofence, setModalGeofence] = useState(false);
  const [editandoGeofence, setEditandoGeofence] = useState(null);
  const [novaGeofence, setNovaGeofence] = useState({
    empresa_id: '1',
    nome: '',
    latitude: '',
    longitude: '',
    raio_metros: 100,
    endereco: '',
    ativo: true
  });
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);
  const { toast } = useToast();

  // Função para abrir Google Maps com as coordenadas
  const abrirNoGoogleMaps = (geo) => {
    const url = `https://www.google.com/maps?q=${geo.latitude},${geo.longitude}`;
    window.open(url, '_blank');
    toast({ title: "Sucesso!", description: `Abrindo ${geo.nome} no Google Maps`, variant: "default" });
  };

  // Função para editar geofence
  const editarGeofence = (geo) => {
    setEditandoGeofence(geo.id);
    setNovaGeofence({
      empresa_id: geo.empresa_id || '1',
      nome: geo.nome,
      latitude: geo.latitude,
      longitude: geo.longitude,
      raio_metros: geo.raio_metros,
      ativo: geo.ativo
    });
    setModalGeofence(true);
  };

  // Função para excluir geofence
  const excluirGeofence = async (geo) => {
    if (!window.confirm(`Tem certeza que deseja excluir a geofence "${geo.nome}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/timetracking/geofences/${geo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Sucesso!", description: 'Geofence excluída com sucesso!', variant: "default" });
      onRefresh();
    } catch (error) {
      console.error('Erro ao excluir geofence:', error);
      toast({ title: "Erro", description: 'Erro ao excluir geofence', variant: "destructive" });
    }
  };

  const criarGeofence = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Se está editando, fazer PUT, senão fazer POST
      if (editandoGeofence) {
        await axios.put(`${API}/api/timetracking/geofences/${editandoGeofence}`, novaGeofence, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Sucesso!", description: 'Geofence atualizada com sucesso!', variant: "default" });
      } else {
        await axios.post(`${API}/api/timetracking/geofences`, novaGeofence, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Sucesso!", description: 'Geofence criada com sucesso!', variant: "default" });
      }
      
      setModalGeofence(false);
      setEditandoGeofence(null);
      setNovaGeofence({
        empresa_id: '1',
        nome: '',
        latitude: '',
        longitude: '',
        raio_metros: 100,
        ativo: true
      });
      onRefresh();
    } catch (error) {
      console.error('Erro ao criar geofence:', error);
      toast({ title: "Erro", description: 'Erro ao criar geofence', variant: "destructive" });
    }
  };

  const obterLocalizacaoAtual = () => {
    if (!navigator.geolocation) {
      toast({ title: "Erro", description: 'Geolocalização não suportada', variant: "destructive" });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNovaGeofence(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }));
        toast({ title: "Sucesso!", description: 'Localização atual capturada!', variant: "default" });
      },
      (error) => {
        toast({ title: "Erro", description: 'Erro ao obter localização', variant: "destructive" });
      }
    );
  };

  const buscarCoordenadaPorEndereco = async () => {
    if (!novaGeofence.endereco || !novaGeofence.endereco.trim()) {
      toast({ title: "Erro", description: 'Por favor, digite um endereço', variant: "destructive" });
      return;
    }
    
    setBuscandoEndereco(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/geolocation/geocode`,
        { endereco: novaGeofence.endereco },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.latitude && response.data.longitude) {
        setNovaGeofence(prev => ({
          ...prev,
          latitude: response.data.latitude.toString(),
          longitude: response.data.longitude.toString()
        }));
        toast({ title: "Sucesso!", description: `Coordenadas encontradas: ${response.data.endereco_formatado || novaGeofence.endereco}`, variant: "default" });
      }
    } catch (error) {
      console.error('Erro ao buscar coordenadas:', error);
      toast({ title: "Erro", description: 'Erro ao buscar coordenadas. Verifique o endereço e tente novamente.', variant: "destructive" });
    } finally {
      setBuscandoEndereco(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Geofences */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Geofences</CardTitle>
            <CardDescription>
              {geofences.length} área(s) de trabalho configurada(s)
            </CardDescription>
          </div>
          <Button onClick={() => {
            setEditandoGeofence(null);
            setNovaGeofence({
              empresa_id: '1',
              nome: '',
              latitude: '',
              longitude: '',
              raio_metros: 100,
              ativo: true
            });
            setModalGeofence(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Geofence
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {geofences.map((geo) => (
              <Card key={geo.id} className="border-l-4 border-l-blue-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold">{geo.nome}</h3>
                      <p className="text-sm text-gray-600">
                        Raio: {geo.raio_metros}m
                      </p>
                      <p className="text-xs text-gray-500">
                        Lat: {geo.latitude}, Lng: {geo.longitude}
                      </p>
                      <p className="text-xs text-gray-500">
                        Criado em: {new Date(geo.criado_em).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={geo.ativo ? 'default' : 'secondary'}>
                        {geo.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                      
                      {/* Botão Abrir no Google Maps */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => abrirNoGoogleMaps(geo)}
                        title="Abrir no Google Maps"
                        className="hover:bg-green-50 hover:border-green-500"
                      >
                        <MapPin className="h-4 w-4 text-green-600" />
                      </Button>
                      
                      {/* Botão Editar */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => editarGeofence(geo)}
                        title="Editar geofence"
                        className="hover:bg-blue-50 hover:border-blue-500"
                      >
                        <Edit className="h-4 w-4 text-blue-600" />
                      </Button>
                      
                      {/* Botão Excluir */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => excluirGeofence(geo)}
                        title="Excluir geofence"
                        className="hover:bg-red-50 hover:border-red-500"
                      >
                        <Trash className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {geofences.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhuma geofence configurada</p>
                <p className="text-sm">Crie uma área de trabalho para controlar a localização</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal Nova/Editar Geofence */}
      {modalGeofence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{editandoGeofence ? 'Editar Geofence' : 'Nova Geofence'}</CardTitle>
              <CardDescription>
                {editandoGeofence ? 'Atualize os dados da área de trabalho' : 'Defina uma área de trabalho'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nome-geo">Nome da Área</Label>
                <Input
                  id="nome-geo"
                  value={novaGeofence.nome}
                  onChange={(e) => setNovaGeofence(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Escritório Central"
                />
              </div>
              
              {/* Campo de Endereço com Google Maps */}
              <div>
                <Label htmlFor="endereco-geo" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Endereço Completo
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="endereco-geo"
                    value={novaGeofence.endereco}
                    onChange={(e) => setNovaGeofence(prev => ({ ...prev, endereco: e.target.value }))}
                    placeholder="Ex: Av. Paulista, 1000 - São Paulo, SP"
                    className="flex-1"
                  />
                  <Button 
                    onClick={buscarCoordenadaPorEndereco}
                    disabled={buscandoEndereco || !novaGeofence.endereco}
                    variant="secondary"
                    type="button"
                  >
                    {buscandoEndereco ? (
                      <>⏳ Buscando...</>
                    ) : (
                      <>🔍 Buscar</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Digite o endereço completo e clique em "Buscar" para obter lat/long automaticamente
                </p>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Ou informe as coordenadas manualmente:</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={novaGeofence.latitude}
                      onChange={(e) => setNovaGeofence(prev => ({ ...prev, latitude: e.target.value }))}
                      placeholder="-23.5505"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={novaGeofence.longitude}
                      onChange={(e) => setNovaGeofence(prev => ({ ...prev, longitude: e.target.value }))}
                      placeholder="-46.6333"
                    />
                  </div>
                </div>
              </div>
              
              <Button onClick={obterLocalizacaoAtual} variant="outline" className="w-full">
                <MapPin className="h-4 w-4 mr-2" />
                Usar Minha Localização Atual
              </Button>
              
              <div>
                <Label htmlFor="raio">Raio (metros)</Label>
                <Input
                  id="raio"
                  type="number"
                  min="1"
                  max="10000"
                  value={novaGeofence.raio_metros}
                  onChange={(e) => setNovaGeofence(prev => ({ ...prev, raio_metros: parseInt(e.target.value) }))}
                />
              </div>
              
              <div className="flex space-x-3">
                <Button onClick={criarGeofence} className="flex-1">
                  {editandoGeofence ? 'Atualizar Geofence' : 'Criar Geofence'}
                </Button>
                <Button 
                  onClick={() => {
                    setModalGeofence(false);
                    setEditandoGeofence(null);
                    setNovaGeofence({
                      empresa_id: '1',
                      nome: '',
                      latitude: '',
                      longitude: '',
                      raio_metros: 100,
                      ativo: true
                    });
                  }} 
                  variant="outline" 
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

const RelatoriosAdmin = ({ funcionarios }) => {
  const [relatorioTipo, setRelatorioTipo] = useState('resumo');
  const [periodoInicio, setPeriodoInicio] = useState(new Date().toISOString().split('T')[0]);
  const [periodoFim, setPeriodoFim] = useState(new Date().toISOString().split('T')[0]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState('todos');
  const [dadosRelatorio, setDadosRelatorio] = useState(null);
  const [loading, setLoading] = useState(false);
  // Estados específicos para AEJ
  const [mesAEJ, setMesAEJ] = useState(new Date().getMonth() + 1);
  const [anoAEJ, setAnoAEJ] = useState(new Date().getFullYear());
  const [arquivosAEJ, setArquivosAEJ] = useState(null);
  const [loadingAEJ, setLoadingAEJ] = useState(false);
  const { toast } = useToast();

  // Log para debug - confirmar que o componente está sendo renderizado com AEJ
  console.log('RelatoriosAdmin renderizado - AEJ disponível:', true);

  const gerarRelatorio = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      switch (relatorioTipo) {
        case 'resumo':
          endpoint = '/api/timetracking/relatorios/resumo-geral';
          break;
        case 'frequencia':
          endpoint = '/api/timetracking/relatorios/frequencia';
          break;
        case 'horas-extras':
          endpoint = '/api/timetracking/relatorios/horas-extras';
          break;
        case 'banco-horas':
          endpoint = '/api/timetracking/relatorios/banco-horas-geral';
          break;
        case 'geolocalizacao':
          endpoint = '/api/timetracking/relatorios/geolocalizacao';
          break;
        case 'aej':
          // AEJ é tratado em função separada
          return;
        default:
          endpoint = '/api/timetracking/relatorios/resumo-geral';
      }

      const params = new URLSearchParams({
        data_inicio: periodoInicio,
        data_fim: periodoFim,
        funcionario_id: funcionarioSelecionado !== 'todos' ? funcionarioSelecionado : ''
      });

      const response = await axios.get(`${API}${endpoint}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDadosRelatorio(response.data);
      toast({ title: "Sucesso!", description: 'Relatório gerado com sucesso!', variant: "default" });
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast({ title: "Erro", description: 'Erro ao gerar relatório administrativo', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar AEJ administrativo
  const gerarAEJAdmin = async () => {
    setLoadingAEJ(true);
    setArquivosAEJ(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (funcionarioSelecionado === 'todos') {
        // Gerar AEJ para todos os funcionários
        const resultados = [];
        
        for (const funcionario of funcionarios) {
          try {
            console.log('Processando funcionário:', funcionario);
            
            const response = await axios.get(
              `${API}/api/timetracking/aej/${funcionario.id}?mes=${mesAEJ}&ano=${anoAEJ}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            resultados.push({
              funcionario: {
                id: funcionario.id,
                nome: funcionario.nome || funcionario.name || `Funcionario_${funcionario.id.substring(0, 8)}`,
                email: funcionario.email || 'N/A'
              },
              aej: response.data
            });
          } catch (error) {
            console.warn(`Erro ao gerar AEJ para ${funcionario.nome || funcionario.id}:`, error);
            resultados.push({
              funcionario: {
                id: funcionario.id,
                nome: funcionario.nome || funcionario.name || `Funcionario_${funcionario.id.substring(0, 8)}`,
                email: funcionario.email || 'N/A'
              },
              erro: error.message || 'Erro ao gerar AEJ'
            });
          }
        }
        
        setArquivosAEJ({
          tipo: 'multiplos',
          arquivos: resultados,
          periodo: `${mesAEJ.toString().padStart(2, '0')}/${anoAEJ}`,
          total_funcionarios: funcionarios.length,
          total_sucessos: resultados.filter(r => !r.erro).length,
          total_erros: resultados.filter(r => r.erro).length
        });
        
        toast({ title: "Sucesso!", description: `AEJ gerado para ${resultados.filter(r => !r.erro).length} funcionários!`, variant: "default" });
        
      } else {
        // Gerar AEJ para funcionário específico
        const response = await axios.get(
          `${API}/api/timetracking/aej/${funcionarioSelecionado}?mes=${mesAEJ}&ano=${anoAEJ}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const funcionario = funcionarios.find(f => f.id === funcionarioSelecionado);
        
        setArquivosAEJ({
          tipo: 'individual',
          funcionario: {
            id: funcionario.id,
            nome: funcionario.nome || funcionario.name || `Funcionario_${funcionario.id.substring(0, 8)}`,
            email: funcionario.email || 'N/A'
          },
          aej: response.data,
          periodo: `${mesAEJ.toString().padStart(2, '0')}/${anoAEJ}`
        });
        
        toast({ title: "Sucesso!", description: 'AEJ gerado com sucesso!', variant: "default" });
      }
      
    } catch (error) {
      console.error('Erro ao gerar AEJ:', error);
      toast({ title: "Erro", description: 'Erro ao gerar Arquivo Eletrônico de Jornada', variant: "destructive" });
    } finally {
      setLoadingAEJ(false);
    }
  };

  // Função para baixar AEJ individual
  const baixarAEJIndividual = (aejData, funcionarioNome) => {
    if (!aejData || !aejData.arquivo_aej) return;
    
    // Validar nome do funcionário e criar nome seguro para arquivo
    const nomeSeguro = funcionarioNome ? 
      funcionarioNome.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') : 
      'Funcionario_Desconhecido';
    
    const blob = new Blob([aejData.arquivo_aej], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AEJ_${nomeSeguro}_${mesAEJ.toString().padStart(2, '0')}_${anoAEJ}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    console.log('Download iniciado:', a.download);
  };

  // Função para baixar todos os AEJs em ZIP (simulado com múltiplos downloads)
  const baixarTodosAEJ = () => {
    if (!arquivosAEJ || arquivosAEJ.tipo !== 'multiplos') return;
    
    arquivosAEJ.arquivos.forEach((resultado, index) => {
      if (resultado.aej && resultado.aej.arquivo_aej) {
        setTimeout(() => {
          baixarAEJIndividual(resultado.aej, resultado.funcionario.nome);
        }, index * 500); // Delay entre downloads para evitar bloqueios do navegador
      }
    });
    
    toast({ title: "Sucesso!", description: `Iniciando download de ${arquivosAEJ.total_sucessos} arquivos AEJ...`, variant: "default" });
  };

  const exportarRelatorio = (formato) => {
    if (!dadosRelatorio) return;
    
    const dataAtual = new Date().toISOString().split('T')[0];
    const nomeArquivo = `relatorio_${relatorioTipo}_${dataAtual}`;
    
    if (formato === 'json') {
      const blob = new Blob([JSON.stringify(dadosRelatorio, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nomeArquivo}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else if (formato === 'pdf' && relatorioTipo === 'geolocalizacao') {
      exportarGeolocalizacaoPDF();
    }
  };

  const exportarGeolocalizacaoPDF = async () => {
    if (!dadosRelatorio || !dadosRelatorio.registros) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/timetracking/relatorios/geolocalizacao/pdf`,
        {
          dados: dadosRelatorio
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_geolocalizacao_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: "Sucesso!", description: 'PDF gerado com sucesso!', variant: "default" });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({ title: "Erro", description: 'Erro ao gerar PDF de geolocalização', variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Controles do Relatório */}
      <Card>
        <CardHeader>
          <CardTitle>Gerar Relatórios Administrativos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="tipo-relatorio">Tipo de Relatório</Label>
              <select
                id="tipo-relatorio"
                value={relatorioTipo}
                onChange={(e) => {
                  console.log('Mudança de tipo de relatório:', e.target.value);
                  setRelatorioTipo(e.target.value);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="resumo">Resumo Geral</option>
                <option value="frequencia">Frequência</option>
                <option value="horas-extras">Horas Extras</option>
                <option value="banco-horas">Banco de Horas</option>
                <option value="geolocalizacao">Geolocalização dos Registros</option>
                <option value="aej">AEJ - Arquivo Eletrônico de Jornada</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="funcionario">Funcionário</Label>
              <select
                id="funcionario"
                value={funcionarioSelecionado}
                onChange={(e) => setFuncionarioSelecionado(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="todos">Todos os Funcionários</option>
                {funcionarios.map((func) => (
                  <option key={func.id} value={func.id}>
                    {func.nome || func.name || `Funcionario ${func.id.substring(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="periodo-inicio">Data Início</Label>
              <Input
                id="periodo-inicio"
                type="date"
                value={periodoInicio}
                onChange={(e) => setPeriodoInicio(e.target.value)}
                disabled={relatorioTipo === 'aej'}
              />
            </div>
            
            <div>
              <Label htmlFor="periodo-fim">Data Fim</Label>
              <Input
                id="periodo-fim"
                type="date"
                value={periodoFim}
                onChange={(e) => setPeriodoFim(e.target.value)}
                disabled={relatorioTipo === 'aej'}
              />
            </div>
          </div>

          {/* Controles específicos para AEJ */}
          {relatorioTipo === 'aej' && (
            <div className="grid grid-cols-2 gap-4 mt-4 p-4 border rounded-lg bg-blue-50">
              <div>
                <Label htmlFor="mes-aej">Mês (AEJ)</Label>
                <select
                  id="mes-aej"
                  value={mesAEJ}
                  onChange={(e) => setMesAEJ(parseInt(e.target.value))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(mes => (
                    <option key={mes} value={mes}>
                      {new Date(2023, mes - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="ano-aej">Ano (AEJ)</Label>
                <select
                  id="ano-aej"
                  value={anoAEJ}
                  onChange={(e) => setAnoAEJ(parseInt(e.target.value))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(ano => (
                    <option key={ano} value={ano}>{ano}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          <div className="flex space-x-3">
            <Button 
              onClick={relatorioTipo === 'aej' ? gerarAEJAdmin : gerarRelatorio} 
              disabled={relatorioTipo === 'aej' ? loadingAEJ : loading}
            >
              {(relatorioTipo === 'aej' ? loadingAEJ : loading) ? (
                <Activity className="h-4 w-4 mr-2 animate-spin" />
              ) : relatorioTipo === 'aej' ? (
                <Download className="h-4 w-4 mr-2" />
              ) : (
                <BarChart3 className="h-4 w-4 mr-2" />
              )}
              {relatorioTipo === 'aej' ? 'Gerar AEJ' : 'Gerar Relatório'}
            </Button>
            
            {dadosRelatorio && (
              <Button onClick={() => exportarRelatorio('json')} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Exportar JSON
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Exibição dos Dados do Relatório */}
      {dadosRelatorio && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Resultados do Relatório - {relatorioTipo}</CardTitle>
                <CardDescription>
                  Período: {periodoInicio} a {periodoFim}
                </CardDescription>
              </div>
              {relatorioTipo === 'geolocalizacao' && (
                <Button 
                  onClick={() => exportarRelatorio('pdf')}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Exportar PDF
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <JsonTabela dados={dadosRelatorio} />

              {/* Dados Brutos */}
              <details className="mt-6">
                <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                  Ver dados detalhados (JSON)
                </summary>
                <pre className="mt-2 p-4 bg-gray-50 rounded-lg text-xs overflow-auto max-h-96">
                  {JSON.stringify(dadosRelatorio, null, 2)}
                </pre>
              </details>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exibição dos Resultados do AEJ */}
      {arquivosAEJ && (
        <Card>
          <CardHeader>
            <CardTitle>
              Arquivos Eletrônicos de Jornada (AEJ) - {arquivosAEJ.periodo}
            </CardTitle>
            <CardDescription>
              {arquivosAEJ.tipo === 'multiplos' 
                ? `${arquivosAEJ.total_sucessos} de ${arquivosAEJ.total_funcionarios} arquivos gerados com sucesso`
                : `Arquivo individual para ${arquivosAEJ.funcionario.nome}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {arquivosAEJ.tipo === 'individual' ? (
              /* AEJ Individual */
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-medium">Funcionário:</div>
                    <div>{arquivosAEJ.funcionario.nome}</div>
                  </div>
                  <div>
                    <div className="font-medium">Email:</div>
                    <div>{arquivosAEJ.funcionario.email}</div>
                  </div>
                  <div>
                    <div className="font-medium">Total de Registros:</div>
                    <div>{arquivosAEJ.aej.total_registros}</div>
                  </div>
                  <div>
                    <div className="font-medium">Total de Ajustes:</div>
                    <div>{arquivosAEJ.aej.total_ajustes}</div>
                  </div>
                </div>
                
                <div>
                  <div className="font-medium mb-2">Hash de Integridade:</div>
                  <code className="text-xs bg-gray-100 p-2 rounded block break-all">
                    {arquivosAEJ.aej.hash_arquivo}
                  </code>
                </div>
                
                <Button 
                  onClick={() => baixarAEJIndividual(arquivosAEJ.aej, arquivosAEJ.funcionario.nome)}
                  className="w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Arquivo AEJ
                </Button>
              </div>
            ) : (
              /* AEJ Múltiplos */
              <div className="space-y-4">
                {/* Resumo */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {arquivosAEJ.total_funcionarios}
                    </div>
                    <div className="text-sm text-gray-600">Total Funcionários</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {arquivosAEJ.total_sucessos}
                    </div>
                    <div className="text-sm text-gray-600">Sucessos</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {arquivosAEJ.total_erros}
                    </div>
                    <div className="text-sm text-gray-600">Erros</div>
                  </div>
                </div>

                {/* Botão para baixar todos */}
                <Button onClick={baixarTodosAEJ} className="w-full" disabled={arquivosAEJ.total_sucessos === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Todos os AEJs ({arquivosAEJ.total_sucessos} arquivos)
                </Button>

                {/* Lista detalhada */}
                <details className="mt-4">
                  <summary className="font-medium cursor-pointer text-blue-600 hover:text-blue-800">
                    Ver detalhes por funcionário ({arquivosAEJ.arquivos.length} funcionários)
                  </summary>
                  <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                    {arquivosAEJ.arquivos.map((resultado, index) => (
                      <div key={index} className={`p-3 rounded-lg border ${resultado.erro ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{resultado.funcionario.nome}</div>
                            <div className="text-sm text-gray-600">{resultado.funcionario.email}</div>
                            {resultado.erro ? (
                              <div className="text-sm text-red-600 mt-1">❌ {resultado.erro}</div>
                            ) : (
                              <div className="text-sm text-green-600 mt-1">
                                ✅ {resultado.aej.total_registros} registros, {resultado.aej.total_ajustes} ajustes
                              </div>
                            )}
                          </div>
                          {!resultado.erro && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => baixarAEJIndividual(resultado.aej, resultado.funcionario.nome)}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Baixar
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ========== COMPONENTE GERAR AFD ==========
const GerarAFD = () => {
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    carregarEmpresas();
  }, []);

  const carregarEmpresas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmpresas(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast({ title: "Erro", description: 'Erro ao carregar empresas', variant: "destructive" });
    }
  };

  const gerarAFD = async () => {
    if (!empresaSelecionada) {
      toast({ title: "Erro", description: 'Selecione uma empresa', variant: "destructive" });
      return;
    }
    if (!dataInicio || !dataFim) {
      toast({ title: "Erro", description: 'Selecione o período', variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/timetracking/gerar-afd`,
        {
          empresa_id: empresaSelecionada,
          data_inicio: dataInicio,
          data_fim: dataFim
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AFD_${empresaSelecionada}_${dataInicio}_${dataFim}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({ title: "Sucesso!", description: 'AFD gerado com sucesso!', variant: "default" });
    } catch (error) {
      console.error('Erro ao gerar AFD:', error);
      toast({ title: "Erro", description: error.response?.data?.detail || 'Erro ao gerar AFD', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Gerar AFD - Arquivo Fonte de Dados</span>
          </CardTitle>
          <CardDescription>
            Arquivo oficial conforme Portaria 671/2021 do MTE. Contém todos os registros de ponto brutos para fiscalização trabalhista.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">📋 O que é o AFD?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Arquivo texto ASCII com registros de ponto brutos</li>
              <li>• Formato padronizado pelo MTE (Portaria 671/2021)</li>
              <li>• Contém: cabeçalho, marcações, ajustes e totalização</li>
              <li>• Deve ser mantido por no mínimo 5 anos</li>
              <li>• Usado pela fiscalização trabalhista</li>
            </ul>
          </div>

          {/* Formulário */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="empresa">Empresa *</Label>
              <select
                id="empresa"
                value={empresaSelecionada}
                onChange={(e) => setEmpresaSelecionada(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Selecione uma empresa</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.name} {empresa.cnpj ? `- ${empresa.cnpj}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dataInicio">Data Início *</Label>
                <Input
                  id="dataInicio"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataFim">Data Fim *</Label>
                <Input
                  id="dataFim"
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </div>
            </div>

            <Button
              onClick={gerarAFD}
              disabled={loading}
              className="w-full"
              size="lg"
            >
              <Download className="h-4 w-4 mr-2" />
              {loading ? 'Gerando AFD...' : 'Gerar e Baixar AFD'}
            </Button>
          </div>

          {/* Info Técnica */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">🔧 Estrutura do AFD</h4>
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>Tipo 1:</strong> Cabeçalho (CNPJ, Razão Social, Nº Série REP)</p>
              <p><strong>Tipo 2:</strong> Inclusão/Alteração de Empregado</p>
              <p><strong>Tipo 3:</strong> Marcação de Ponto</p>
              <p><strong>Tipo 4:</strong> Marcação Pré-assinalada</p>
              <p><strong>Tipo 5:</strong> Ajuste de Relógio</p>
              <p><strong>Tipo 9:</strong> Totalização (Rodapé)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ========== COMPONENTE GERAR AEJ (PTRP) ==========
const GerarAEJ = () => {
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    carregarEmpresas();
  }, []);

  const carregarEmpresas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmpresas(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast({ title: "Erro", description: 'Erro ao carregar empresas', variant: "destructive" });
    }
  };

  const gerarAEJ = async () => {
    if (!empresaSelecionada) {
      toast({ title: "Erro", description: 'Selecione uma empresa', variant: "destructive" });
      return;
    }
    if (!dataInicio || !dataFim) {
      toast({ title: "Erro", description: 'Selecione o período', variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/timetracking/gerar-aej`,
        {
          empresa_id: empresaSelecionada,
          data_inicio: dataInicio,
          data_fim: dataFim
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AEJ_${empresaSelecionada}_${dataInicio}_${dataFim}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({ title: "Sucesso!", description: 'AEJ gerado com sucesso!', variant: "default" });
    } catch (error) {
      console.error('Erro ao gerar AEJ:', error);
      toast({ title: "Erro", description: error.response?.data?.detail || 'Erro ao gerar AEJ', variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Download className="h-5 w-5" />
            <span>PTRP - Gerar AEJ (Arquivo Eletrônico de Jornada)</span>
          </CardTitle>
          <CardDescription>
            Arquivo tratado conforme Portaria 671/2021. Gerado através do PTRP (Programa de Tratamento de Registros de Ponto).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Box */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">🔄 O que é o PTRP e AEJ?</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• <strong>PTRP:</strong> Programa que processa os registros brutos (AFD)</li>
              <li>• <strong>AEJ:</strong> Arquivo com jornadas tratadas e calculadas</li>
              <li>• Contém: horas trabalhadas, extras, faltas, atrasos</li>
              <li>• Cálculo de intervalos e banco de horas</li>
              <li>• Formato oficial do MTE (Portaria 671/2021 - Anexo VI)</li>
              <li>• Usado para folha de pagamento e auditorias</li>
            </ul>
          </div>

          {/* Formulário */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="empresaAEJ">Empresa *</Label>
              <select
                id="empresaAEJ"
                value={empresaSelecionada}
                onChange={(e) => setEmpresaSelecionada(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Selecione uma empresa</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.name} {empresa.cnpj ? `- ${empresa.cnpj}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dataInicioAEJ">Data Início *</Label>
                <Input
                  id="dataInicioAEJ"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataFimAEJ">Data Fim *</Label>
                <Input
                  id="dataFimAEJ"
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </div>
            </div>

            <Button
              onClick={gerarAEJ}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
            >
              <Download className="h-4 w-4 mr-2" />
              {loading ? 'Processando e Gerando AEJ...' : 'Processar e Gerar AEJ'}
            </Button>
          </div>

          {/* Processo PTRP */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">⚙️ Processo do PTRP</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">1</div>
                <div className="text-sm">
                  <strong>Coleta de Registros:</strong> Busca todos os registros de ponto do período
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">2</div>
                <div className="text-sm">
                  <strong>Cálculo de Jornadas:</strong> Calcula horas trabalhadas por dia
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">3</div>
                <div className="text-sm">
                  <strong>Análise de Intervalos:</strong> Valida intervalos obrigatórios (almoço, etc)
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">4</div>
                <div className="text-sm">
                  <strong>Horas Extras:</strong> Identifica e calcula horas extras e noturnas
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0">5</div>
                <div className="text-sm">
                  <strong>Geração AEJ:</strong> Gera arquivo no formato oficial do MTE
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* NOVO CARD: Importar AEJ */}
      <ImportarAEJ />
    </div>
  );
};

// ========== COMPONENTE IMPORTAR AEJ ==========
const ImportarAEJ = () => {
  const [loading, setLoading] = useState(false);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const [resultado, setResultado] = useState(null);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.txt')) {
        toast({ title: "Erro", description: 'Por favor, selecione um arquivo .txt', variant: "destructive" });
        return;
      }
      setArquivoSelecionado(file);
      setResultado(null); // Limpar resultado anterior
    }
  };

  const importarAEJ = async () => {
    console.log('🔵 importarAEJ chamado');
    console.log('📁 Arquivo selecionado:', arquivoSelecionado);
    
    if (!arquivoSelecionado) {
      console.log('❌ Nenhum arquivo selecionado');
      toast({ title: "Erro", description: 'Por favor, selecione um arquivo AEJ (.txt)', variant: "destructive" });
      return;
    }

    setLoading(true);
    console.log('⏳ Loading iniciado');
    
    try {
      // Ler conteúdo do arquivo
      const reader = new FileReader();
      
      reader.onerror = (error) => {
        console.error('❌ Erro ao ler arquivo:', error);
        toast({ title: "Erro", description: 'Erro ao ler o arquivo', variant: "destructive" });
        setLoading(false);
      };
      
      reader.onload = async (event) => {
        console.log('📄 Arquivo lido com sucesso');
        const conteudoArquivo = event.target.result;
        console.log('📊 Tamanho do conteúdo:', conteudoArquivo.length, 'caracteres');
        console.log('📝 Primeiras 200 caracteres:', conteudoArquivo.substring(0, 200));

        try {
          const token = localStorage.getItem('token');
          console.log('🔑 Token:', token ? 'Presente' : 'Ausente');
          
          console.log('📡 Enviando requisição para:', `${API}/api/timetracking/importar-aej`);
          
          const response = await axios.post(
            `${API}/api/timetracking/importar-aej`,
            conteudoArquivo,  // Enviar string diretamente, não objeto JSON
            {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'text/plain'  // Mudado de application/json para text/plain
              }
            }
          );

          console.log('✅ Resposta recebida:', response.data);
          setResultado(response.data);
          
          if (response.data.success) {
            toast({ title: "Sucesso!", description: `✅ ${response.data.estatisticas.marcacoes_importadas} marcações importadas com sucesso`, variant: "default" });
          } else {
            toast({ title: "Erro", description: '⚠️ Importação com avisos. Verifique os detalhes abaixo', variant: "destructive" });
          }
        } catch (error) {
          console.error('❌ Erro na requisição:', error);
          console.error('❌ Detalhes do erro:', error.response?.data);
          toast({ title: "Erro", description: error.response?.data?.detail || 'Erro ao processar arquivo AEJ', variant: "destructive" });
        } finally {
          setLoading(false);
          console.log('⏳ Loading finalizado');
        }
      };

      console.log('📖 Iniciando leitura do arquivo...');
      reader.readAsText(arquivoSelecionado);
    } catch (error) {
      console.error('❌ Erro geral:', error);
      toast({ title: "Erro", description: 'Não foi possível ler o arquivo selecionado', variant: "destructive" });
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>Importar AEJ (Validação Round-Trip)</span>
        </CardTitle>
        <CardDescription>
          Faça upload de um arquivo AEJ para popular a tabela de registros de ponto. Útil para validar se o AEJ gerado está correto.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">🔄 Validação Round-Trip</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Objetivo:</strong> Verificar se o AEJ gerado está correto</li>
            <li>• <strong>Processo:</strong> Gerar AEJ → Importar AEJ → Validar dados</li>
            <li>• <strong>Benefícios:</strong> Testar conformidade com Portaria 671/2021</li>
            <li>• <strong>Duplicatas:</strong> Sistema detecta e ignora marcações já existentes</li>
          </ul>
        </div>

        {/* Upload de Arquivo */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="aejFile">Selecione o arquivo AEJ (.txt) *</Label>
            <Input
              id="aejFile"
              type="file"
              accept=".txt"
              onChange={handleFileChange}
              className="cursor-pointer"
            />
            {arquivoSelecionado && (
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                📄 Arquivo selecionado: <strong>{arquivoSelecionado.name}</strong> ({(arquivoSelecionado.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>

          <Button
            onClick={importarAEJ}
            disabled={loading || !arquivoSelecionado}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            {loading ? 'Importando AEJ...' : 'Importar e Processar AEJ'}
          </Button>
        </div>

        {/* Resultado da Importação */}
        {resultado && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-900 mb-3">📊 Resultado da Importação</h4>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white p-3 rounded border">
                <div className="text-xs text-gray-500 mb-1">Linhas Processadas</div>
                <div className="text-2xl font-bold text-gray-900">{resultado.estatisticas.linhas_processadas}</div>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <div className="text-xs text-green-600 mb-1">Marcações Importadas</div>
                <div className="text-2xl font-bold text-green-700">{resultado.estatisticas.marcacoes_importadas}</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <div className="text-xs text-yellow-600 mb-1">Duplicadas (Ignoradas)</div>
                <div className="text-2xl font-bold text-yellow-700">{resultado.estatisticas.marcacoes_duplicadas}</div>
              </div>
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <div className="text-xs text-red-600 mb-1">Erros</div>
                <div className="text-2xl font-bold text-red-700">{resultado.estatisticas.total_erros}</div>
              </div>
            </div>

            {resultado.detalhes && (
              <div className="space-y-2">
                <div className="text-sm">
                  <strong>Empresa:</strong> {resultado.detalhes.empresa_cnpj || 'N/A'}
                </div>
                <div className="text-sm">
                  <strong>Funcionário ID:</strong> {resultado.detalhes.funcionario_id || 'N/A'}
                </div>
                <div className="text-sm">
                  <strong>Período:</strong> {
                    resultado.detalhes.periodo 
                      ? (typeof resultado.detalhes.periodo === 'object' 
                          ? `${resultado.detalhes.periodo.mes}/${resultado.detalhes.periodo.ano}` 
                          : resultado.detalhes.periodo)
                      : 'N/A'
                  }
                </div>
              </div>
            )}

            {resultado.erros && resultado.erros.length > 0 && (
              <div className="mt-4">
                <h5 className="font-semibold text-red-900 mb-2">⚠️ Erros encontrados:</h5>
                <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                  <ul className="text-sm text-red-800 space-y-1">
                    {resultado.erros.map((erro, idx) => (
                      <li key={idx}>• {erro}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeTrackingAdminPage;
